import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Building, Users, TrendingUp, TrendingDown, FileText, Clock, MapPin,
    Search, RefreshCw, ArrowUp, ArrowDown, ChevronsUpDown, Landmark, ChevronRight, ChevronLeft, ChevronDown, ArrowUpDown, ExternalLink, Info
} from 'lucide-react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';

import { useSelector } from 'react-redux';
import api from '../../../api/axios';
import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import PublicSignalFetchModal from '../../../components/Modals/PublicSignalFetchModal';
import { getDaysAgo, renderCompanyAvatar, renderBookmark } from '../../../components/HiringSignalsPage/HSContent/HSCard';
import PublicSignalDetailPage from './PublicSignalDetailPage';

/**
 * US public companies whose own SEC filings show headcount moving.
 *
 * Sibling to the Hiring Signals board, deliberately built on the same table,
 * header chips and modal shell — two boards answering related questions should
 * not feel like two products.
 *
 * WHERE IT DIFFERS, AND WHY
 *
 * That board finds companies with a job posted now, discovered through
 * TheirStack at a credit cost per scan. This reads filings and earnings calls
 * already collected for all 6,292 US-listed companies: instant, free, visible
 * months before a posting exists, and able to show who is CUTTING — which a job
 * board structurally cannot, because a company that is shrinking posts nothing.
 *
 * The columns differ for the same reason. Hiring Signals scores confidence
 * because job-posting evidence needs interpreting; here a filing states a
 * number, so the number is shown. Turning "3,180 employees, down 9.4%" into a
 * score out of ten would discard the best thing about it.
 */

const HEADER_PILL = 'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide shadow-sm text-white whitespace-nowrap';

/*
 * Said on every part of the view toggle.
 *
 * "Table" and "Cards" are two renderings of one result set, but a control that
 * sits beside Filters looks like a filter — so it has to say outright that it
 * changes nothing about which companies are listed.
 */
const VIEW_HINT = 'Changes how results are shown, not which results. Same companies, same filters, same order.';

const SOURCE_META = {
    '10-K': { label: '10-K', tone: 'bg-blue-100 text-blue-700', title: 'Annual report — audited headcount, filed with the SEC' },
    '10-Q': { label: '10-Q', tone: 'bg-sky-100 text-sky-700', title: 'Quarterly report' },
    '8-K': { label: '8-K', tone: 'bg-amber-100 text-amber-700', title: 'Material event — layoffs must be filed here by law' },
    transcript: { label: 'Call', tone: 'bg-violet-100 text-violet-700', title: "Earnings call — an executive's own words" },
};

/**
 * One company with its evidence already open.
 *
 * WHY THIS EXISTS ALONGSIDE THE TABLE
 *
 * The table answers "which companies", and it is good at that — sortable
 * columns, a dozen facts per row, fifty rows on a screen. What it cannot do is
 * show what a company actually SAID, because a quote does not fit in a cell. So
 * reading the evidence meant opening every company in turn.
 *
 * This view trades the columns for the quotes. Filter to a department and the
 * statements are simply there, which is the point: the reason to act on a
 * company is the sentence, not the row.
 *
 * Everything here is already in the list payload — each row carries its sources
 * with a quote apiece — so this costs no extra request.
 */
const SignalCard = ({ row, onOpen }) => {
    // Newest first, and only the documents that actually said something.
    const quotes = (row.sources || [])
        .filter((sc) => sc.quote)
        .sort((a, b) => new Date(b.documentDate || 0) - new Date(a.documentDate || 0))
        .slice(0, 3);

    const pct = /([+-]?\d+(?:\.\d+)?)\s*%/.exec(row.magnitude || '');
    const delta = pct ? Number(pct[1]) : null;

    return (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-[#C7D0F8] transition-colors">
            <div className="flex items-start gap-3 px-5 py-3.5 border-b border-gray-100 flex-wrap">
                {renderCompanyAvatar(null, row.companyName)}
                <div className="min-w-0 flex-1">
                    <button onClick={() => onOpen(row.ticker)}
                            className="text-[14px] font-bold text-gray-900 hover:text-[#3F57E0] transition-colors text-left truncate max-w-full block">
                        {row.companyName}
                    </button>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5 text-[11.5px] text-gray-500">
                        <span className="font-mono">{row.ticker}</span>
                        {row.exchange && <><span className="text-gray-300">·</span><span>{row.exchange}</span></>}
                        {row.industry && <><span className="text-gray-300">·</span><span>{row.industry}</span></>}
                        {row.department && (
                            <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold">
                                {row.department}
                            </span>
                        )}
                    </div>
                </div>
                {row.magnitude && (
                    <span className={`px-2.5 py-1 rounded-lg text-[11.5px] font-bold shrink-0 ${
                        delta === null ? 'bg-slate-50 text-slate-700'
                        : delta < 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
                    }`}>
                        {row.magnitude}
                    </span>
                )}
                <button onClick={() => onOpen(row.ticker)}
                        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-[#3F57E0] border border-[#3F57E0] rounded-md hover:bg-[#EEF1FE] transition-colors">
                    Details <ChevronRight className="w-3 h-3" />
                </button>
            </div>

            <div className="divide-y divide-gray-100">
                {quotes.map((sc, i) => {
                    const meta = SOURCE_META[sc.docType] || { label: sc.docType, tone: 'bg-gray-100 text-gray-600' };
                    return (
                        <div key={`${sc.docType}-${i}`} className="px-5 py-3">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.tone}`} title={meta.title}>
                                    {meta.label}
                                </span>
                                <span className="text-[11px] text-gray-500">{sc.periodKey}</span>
                                {sc.documentDate && (
                                    <span className="text-[11px] text-gray-400">{getDaysAgo(sc.documentDate)}</span>
                                )}
                                {sc.sourceUrl && (
                                    <a href={sc.sourceUrl} target="_blank" rel="noopener noreferrer"
                                       onClick={(e) => e.stopPropagation()}
                                       className="ml-auto text-[11px] font-semibold text-[#3F57E0] hover:underline">
                                        Filing
                                    </a>
                                )}
                            </div>
                            <blockquote className="pl-3 border-l-[3px] border-emerald-400 text-[12.5px] leading-relaxed text-gray-700">
                                &ldquo;{sc.quote}&rdquo;
                            </blockquote>
                        </div>
                    );
                })}

                {/* Why and when, when the filing bothered to say. */}
                {(row.rationale || row.timeframe) && (
                    <div className="px-5 py-2.5 flex flex-wrap gap-2 bg-gray-50/60">
                        {row.rationale && (
                            <span className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">
                                <span className="font-bold uppercase tracking-wide text-[9px]">Why</span> {row.rationale}
                            </span>
                        )}
                        {row.timeframe && (
                            <span className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1 rounded-full bg-red-50 text-red-800 border border-red-100">
                                <span className="font-bold uppercase tracking-wide text-[9px]">When</span> {row.timeframe}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * The board opens on a sample, not an empty state.
 *
 * Reading these signals costs nothing — no credits, no fetch, the sweeps already
 * collected everything — so making the user answer a question before seeing
 * anything was gating for no reason. With no industry chosen the query samples
 * the most recent signals rather than scanning all 6,292 companies, which is
 * what makes opening straight onto results affordable.
 *
 * Module scope so the reference is stable: it is a dependency of the mount
 * effect, and rebuilding it each render would refetch continuously.
 */
const DEFAULTS = { industries: [], departments: [], skills: [], locations: [], direction: 'hiring', query: '', freshDays: 0, exchanges: [], evidence: 'any' };

/**
 * "Not done yet" and "Measured" cannot both hold.
 *
 * A pending cut exists only because the company announced one, so the two
 * together describe an empty set. Left alone, the chip advertised 262 and
 * clicking it returned nothing — the count was right for the direction and
 * wrong for the pair. Switching into that view moves the evidence filter to
 * Stated rather than silently showing an empty list.
 */
const reconcileEvidence = (criteria) => (
    criteria.direction === 'planned' && criteria.evidence === 'measured'
        ? { ...criteria, evidence: 'stated' }
        : criteria
);

// Rows per page. 100 by default: enough to scan an industry without paging,
// small enough that the un-virtualised table stays responsive — the full
// unfiltered set is 2,064 rows and renders sluggishly all at once.
const PAGE_SIZES = [50, 100, 250, 500];

/** Header chip with its sort affordance built in — same behaviour as Hiring Signals. */
const HeaderPill = ({ header }) => {
    const meta = header.column.columnDef.meta || {};
    const Icon = meta.icon;
    const canSort = header.column.getCanSort();
    const sorted = header.column.getIsSorted();

    const chip = (
        <span className={`${HEADER_PILL} ${meta.pill} ${canSort ? 'transition-all duration-150 group-hover:brightness-110 group-hover:shadow' : ''} ${sorted ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}>
            {Icon ? <Icon className="w-3 h-3 shrink-0" /> : null}
            {meta.label}
            {canSort ? (
                sorted === 'asc' ? <ArrowUp className="w-3 h-3 shrink-0" />
                    : sorted === 'desc' ? <ArrowDown className="w-3 h-3 shrink-0" />
                        : <ChevronsUpDown className="w-3 h-3 shrink-0 text-white/50 group-hover:text-white transition-colors" />
            ) : null}
        </span>
    );

    if (!canSort) return chip;
    return (
        <button type="button" onClick={header.column.getToggleSortingHandler()} className="group cursor-pointer">
            {chip}
        </button>
    );
};

// Width of the header pill each column shows, so the loading header has the
// same rhythm as the real one instead of seven identical blocks.
const SKELETON_HEADER_W = {
    company: 104, headcount: 96, change: 98, direction: 96, industry: 96,
    exchange: 84, sources: 96, filed: 78, actions: 80, due: 104,
    basis: 74, affected: 96,
};

/**
 * One loading cell, shaped like the column it stands in.
 *
 * Deliberately not a generic grey bar per cell. A skeleton earns its keep by
 * telling the reader what is coming and where — a row that already shows "logo,
 * two lines of text, a number with a pill beside it, two badges, a button" is
 * legible before any data arrives, and nothing shifts when it does.
 */
const SkeletonCell = ({ id, row }) => {
    // Varied by row so the column does not read as a printed ruler. Deterministic
    // rather than random: re-rendering must not make the bars twitch.
    const jitter = (base, step = 12) => base + ((row * 7) % 3) * step;

    switch (id) {
        case 'company':
            return (
                <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg cc-skel shrink-0" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="h-[9px] rounded cc-skel" style={{ width: `${jitter(58, 14)}%` }} />
                        <div className="h-[7px] rounded cc-skel opacity-60" style={{ width: '32%' }} />
                    </div>
                </div>
            );
        case 'headcount':
            return <div className="h-[9px] rounded cc-skel ml-auto" style={{ width: jitter(58, 10) }} />;
        case 'change':
            return <div className="h-5 w-12 rounded-md cc-skel ml-auto" />;
        case 'direction':
            return <div className="h-5 w-16 rounded-full cc-skel mx-auto" />;
        case 'basis':
            return <div className="h-[18px] w-16 rounded cc-skel mx-auto" />;
        case 'affected':
            // Two lines, since this column wraps.
            return (
                <div className="space-y-1.5">
                    <div className="h-[8px] rounded cc-skel" style={{ width: '92%' }} />
                    <div className="h-[8px] rounded cc-skel" style={{ width: `${jitter(45, 15)}%` }} />
                </div>
            );
        case 'sources':
            return (
                <div className="flex items-center justify-center gap-1">
                    {Array.from({ length: 2 + (row % 2) }).map((_, i) => (
                        <div key={i} className="h-[18px] w-9 rounded cc-skel" />
                    ))}
                </div>
            );
        case 'due':
        case 'filed':
            // Two lines in the real cell: "7mo ago" over "Q1 2026".
            return (
                <div className="space-y-1.5">
                    <div className="h-[9px] rounded cc-skel" style={{ width: jitter(46, 8) }} />
                    <div className="h-[7px] rounded cc-skel opacity-60" style={{ width: 38 }} />
                </div>
            );
        case 'actions':
            return <div className="h-7 w-[88px] rounded-md cc-skel ml-auto" />;
        default:
            return <div className="h-[9px] rounded cc-skel" style={{ width: `${jitter(56, 16)}%` }} />;
    }
};

/**
 * Loading state for the signal table.
 *
 * Renders a real <table> with the SAME colgroup as the live one rather than a
 * flexbox approximation. The previous version hardcoded pixel widths that had
 * drifted from the column defs, so the placeholder bars sat under the wrong
 * headers and every column jumped sideways the moment rows arrived.
 */
const TableSkeleton = ({ rows = 8, columns = [], minWidth }) => {
    const cols = columns.length
        ? columns
        : ['company', 'change', 'industry', 'exchange', 'sources', 'filed', 'actions']
            .map((id) => ({ id, size: SKELETON_HEADER_W[id] + 40 }));

    const centered = (id) => ['sources', 'actions', 'direction', 'basis'].includes(id);
    const totalSize = cols.reduce((sum, c) => sum + (c.size || 120), 0) || 1;

    return (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden" aria-busy="true" aria-live="polite">
            <span className="sr-only">Loading signals…</span>
            <table className="w-full table-fixed" style={{ minWidth: 940 }}>
                <colgroup>
                    {cols.map((c) => (
                        <col key={c.id} style={{ width: `${((c.size / totalSize) * 100).toFixed(3)}%` }} />
                    ))}
                </colgroup>
                <thead>
                    <tr className="border-b border-gray-200">
                        {cols.map((c, i) => (
                            <th key={c.id} className={`py-3 ${i === 0 ? 'pl-5 pr-2' : i === cols.length - 1 ? 'pl-3 pr-5' : 'px-3'}`}>
                                <div className={`flex ${centered(c.id) ? 'justify-center' : c.id === 'change' ? 'justify-end' : ''}`}>
                                    <div className="h-[26px] rounded-full cc-skel" style={{ width: SKELETON_HEADER_W[c.id] || 88 }} />
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: rows }).map((_, r) => (
                        <tr
                            key={r}
                            className="border-b border-gray-100 last:border-0"
                            // Rows fade in on a stagger, so the block reads as a list
                            // filling in rather than one grey slab appearing at once.
                            style={{ animation: `cc-skel-in 320ms ease-out ${r * 45}ms both` }}
                        >
                            {cols.map((c, i) => (
                                <td key={c.id} className={`py-[13px] ${i === 0 ? 'pl-5 pr-2' : i === cols.length - 1 ? 'pl-3 pr-5' : 'px-3'}`}>
                                    <SkeletonCell id={c.id} row={r} />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};


/**
 * The window a headcount change was measured over.
 *
 * "+18.7%" is not interpretable on its own — strong growth over a year, modest
 * over three. Both endpoints are shown rather than just the duration, because a
 * reader checking against a filing needs the actual dates.
 */
// A one-year window is the norm and is stated in the column header instead.
const isOneYear = (span) => span?.label === '1y';

const formatSpan = (from, to) => {
    if (!from || !to) return null;
    const a = new Date(from);
    const b = new Date(to);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;

    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const months = Math.max(1, Math.round((b - a) / (30.44 * 86400000)));
    const label = months >= 12 && months % 12 === 0
        ? `${months / 12}y`
        : months >= 18 ? `${(months / 12).toFixed(1)}y` : `${months}m`;

    return { from: fmt(a), to: fmt(b), label };
};

/**
 * The headcount change, parsed from the stored magnitude string.
 *
 * Parsed rather than stored separately so the badge and the sentence below it
 * can never disagree — they come from the same text.
 */
/**
 * The headcount cell.
 *
 * `magnitude` arrives in two shapes. A 10-K comparison is tidy — "3,797
 * employees (from 3,200, +18.7%)" — but an 8-K states it in prose:
 * "approximately 16% of our global full-time employees". Rendering the second
 * raw overflowed the column and bled across the company name, and prefixing "+"
 * to its unsigned percentage showed Snap's 16% CUT as "+16%".
 *
 * So the sign comes from the row's direction unless the string states one, and
 * the label is capped — the full text is a click away on the detail page.
 */
/**
 * The evidence behind a firing row.
 *
 * 961 of the 1,673 companies in the firing list have no statement about cutting
 * anyone — only a year-over-year headcount decline. That is a real signal but a
 * different claim, and a decline can be attrition, a hiring freeze or a
 * divestiture; one company's entire evidence is "our Philippines subsidiary was
 * sold". Labelling both as "Firing" overstates two-thirds of the list, so the
 * row says which it is.
 */
const EVIDENCE_HELP = {
    cutStated: 'Stated — the company says in its own filings or on an earnings call that it is reducing headcount.',
    cutMeasured: 'Measured — headcount fell year over year, but nothing states that anyone was cut. It may be attrition, a hiring freeze or a business being sold.',
    hireStated: 'Stated — the company says it is hiring, plans to hire, or reports having hired people.',
    hireMeasured: 'Measured — headcount rose year over year, but nothing states that anyone was hired. It may be an acquisition rather than open roles.'
};

/**
 * Badge with a real tooltip, rendered into document.body.
 *
 * Not a CSS-positioned popover: the table sits inside an `overflow-x-auto`
 * container, which clips vertically as well, so a panel positioned inside a row
 * is cut off at the row boundary. A portal plus `position: fixed` escapes the
 * container entirely. The native `title` attribute was the first attempt and is
 * too slow and too faint to count as an explanation.
 */
const EvidenceKind = ({ states, firing }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState(null);

    const show = () => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        // Flipped above the badge near the bottom of the window, and pulled
        // left when it would run off the right edge.
        const width = 280;
        const below = r.bottom + 8;
        const flip = below + 90 > window.innerHeight;
        setPos({
            top: flip ? r.top - 8 : below,
            left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
            flip
        });
    };
    const hide = () => setPos(null);

    return (
        <>
            <span
                ref={ref}
                tabIndex={0}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                className={`shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9.5px] font-bold cursor-help outline-none focus:ring-2 focus:ring-[#3F57E0]/40 ${
                    !states
                        ? 'bg-slate-100 text-slate-500'
                        : firing
                            ? 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200'
                            : 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'
                }`}
            >
                {states ? 'Stated' : 'Measured'}
                <Info className="w-2.5 h-2.5 opacity-60" />
            </span>
            {pos && createPortal(
                <div
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        width: 280,
                        transform: pos.flip ? 'translateY(-100%)' : undefined,
                        zIndex: 9999
                    }}
                    className="rounded-lg bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-xl pointer-events-none"
                >
                    {firing
                        ? (states ? EVIDENCE_HELP.cutStated : EVIDENCE_HELP.cutMeasured)
                        : (states ? EVIDENCE_HELP.hireStated : EVIDENCE_HELP.hireMeasured)}
                </div>,
                document.body
            )}
        </>
    );
};

/**
 * The two halves of a headcount comparison, as separate cells.
 *
 * "3,180 employees  -9.4%" was one cell carrying a count, a unit and a delta.
 * The unit repeats identically on every row, so it belongs in the header, and
 * the count and the change are different quantities a reader sorts by
 * separately — one answers "how big", the other "which way".
 */
const parseChange = (row, isFiring) => {
    const text = String(row.magnitude || '');
    if (!text) return null;

    const pctMatch = text.match(/([+-]?)(\d+(?:\.\d+)?)%/);
    // The bare number, with its unit dropped — the header carries "employees".
    const headMatch = text.match(/^([\d,]+)\s+(?:full[- ]time\s+)?(?:employees|people|team members)/i);

    return {
        text,
        head: headMatch ? headMatch[1] : null,
        pct: pctMatch ? pctMatch[2] : null,
        // An explicit sign wins; otherwise the row's direction decides, so a
        // stated cut never renders as growth.
        sign: pctMatch ? (pctMatch[1] === '-' ? '-' : pctMatch[1] === '+' ? '+' : (isFiring ? '-' : '+')) : null,
        span: formatSpan(row.priorPeriodEnd, row.periodEnd)
    };
};

const renderHeadcount = (row, isFiring) => {
    const c = parseChange(row, isFiring);
    if (!c) return <span className="text-gray-300 text-[12px]">—</span>;
    return c.head
        ? <span className="text-[12.5px] font-semibold text-gray-800 tabular-nums">{c.head}</span>
        : <span className="text-[11.5px] text-gray-500 truncate block" title={c.text}>{c.text}</span>;
};

const renderDelta = (row, isFiring) => {
    const c = parseChange(row, isFiring);
    if (!c || !c.pct) return <span className="text-gray-300 text-[12px]">—</span>;
    return (
        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
            <span
                title={c.span ? `${c.span.from} → ${c.span.to}` : undefined}
                className={`shrink-0 px-1.5 py-0.5 rounded text-[10.5px] font-bold ${isFiring ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}
            >
                {c.sign}{c.pct}%
            </span>
            {/* 99.7% of comparisons span exactly one year, so the header states
                it once. Only the rows measured over some other window carry a
                label — repeating "1y" everywhere hid the exceptions. */}
            {c.span && !isOneYear(c.span) && (
                <span
                    title={`${c.span.from} → ${c.span.to}`}
                    className="shrink-0 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9.5px] font-bold"
                >
                    {c.span.label}
                </span>
            )}
        </div>
    );
};


/**
 * Read the view state out of the URL.
 *
 * The list lives at a URL that fully describes it, so returning from a company
 * detail page restores the same page of the same filter rather than resetting to
 * the default view — previously Back remounted the list and refetched page one
 * of hiring, losing wherever the user was. It also makes a filtered view
 * shareable, which the modal-only version could not be.
 */
const criteriaFromParams = (params) => ({
    industries: (params.get('industries') || '').split(',').map((x) => x.trim()).filter(Boolean),
    departments: (params.get('departments') || '').split(',').map((x) => x.trim()).filter(Boolean),
    skills: (params.get('skills') || '').split(',').map((x) => x.trim()).filter(Boolean),
    locations: (params.get('locations') || '').split(',').map((x) => x.trim()).filter(Boolean),
    // Defaults to 'any': a headcount that moved without comment is still a real
    // signal, so neither half is hidden unless the reader asks.
    evidence: ['stated', 'measured'].includes(params.get('evidence')) ? params.get('evidence') : 'any',
    /*
     * Always hiring on this board — a pasted ?direction=firing must not open the
     * cutting list. The admin panel serves those.
     */
    direction: 'hiring',
    query: params.get('q') || '',
    freshDays: Number.parseInt(params.get('freshDays'), 10) || 0,
    exchanges: (params.get('exchanges') || '').split(',').map((x) => x.trim()).filter(Boolean),
});

const pagingFromParams = (params) => ({
    page: Math.max(Number.parseInt(params.get('page'), 10) || 0, 0),
    pageSize: PAGE_SIZES.includes(Number(params.get('limit'))) ? Number(params.get('limit')) : 100,
});

// Where the list was scrolled to, per URL. sessionStorage rather than state
// because the component unmounts when a company is opened — the value has to
// outlive it, but not the tab.
const SCROLL_KEY = 'cc_publicsignals_scroll';

/**
 * The industry counts, cached for the tab.
 *
 * These drive the Hiring / Firing / Both chip numbers and the "N companies"
 * total, and they cost a 6-8 second aggregation over every signal row. Fetching
 * them on mount meant that coming Back from a company page re-ran the whole
 * thing, and for those seconds the chips rendered a literal "Hiring 0 · Firing 0
 * · Both 0" beside a table full of rows — a confident wrong answer rather than a
 * loading state.
 *
 * Safe to cache: the sweeps run weekly, so the numbers cannot move within a
 * session. The TTL is only a backstop for a tab left open for days.
 */
const INDUSTRIES_KEY = 'cc_publicsignals_industries';
const INDUSTRIES_TTL_MS = 60 * 60 * 1000;

const readCache = (key, field) => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (!parsed?.at || !Array.isArray(parsed[field])) return null;

        /**
         * The cached rows must carry every count the pickers read.
         *
         * A cache keyed on nothing but its name cannot tell that the SHAPE of
         * the answer has changed. When `planned` was added, tabs holding an
         * entry from before it existed kept serving rows without it — every
         * industry and department read 0 in that mode and rendered disabled, so
         * the view could not be filtered at all. Treating a short row as a miss
         * refetches instead.
         */
        const [first] = parsed[field];
        if (first && typeof first.planned !== 'number') return null;

        return Date.now() - parsed.at > INDUSTRIES_TTL_MS ? null : parsed;
    } catch {
        // Private-mode or corrupt entry — fall through to a normal fetch.
        return null;
    }
};

const readIndustriesCache = () => readCache(INDUSTRIES_KEY, 'industries');

/**
 * Applied-filter chips — what the current result set is narrowed by.
 *
 * Tinted in the brand colour rather than grey. In grey they were the same
 * neutral as the direction toggle's own track sitting immediately to their left,
 * so a row that is really "two controls, then two pieces of state" read as one
 * undifferentiated strip of pills. The tint marks them as conditions in force.
 */
const FILTER_CHIP = 'px-3 py-1.5 rounded-full bg-[#EEF1FE] text-[#3F57E0] ring-1 ring-inset ring-[#3F57E0]/15 text-[12px] font-semibold';

/**
 * Chip totals per filter combination, for the life of the tab.
 *
 * Module scope rather than state: the component unmounts when a company is
 * opened, and recomputing these on the way back costs seconds for an answer
 * that cannot have changed. Small and bounded — one entry per filter
 * combination a user actually tries in a session.
 */
const COUNTS_CACHE = new Map();

/**
 * Every figure a cached counts entry must carry to be usable.
 *
 * A cache keyed only on the filters cannot tell that the SHAPE of the answer has
 * changed. When `planned` was added, tabs already holding an entry from before
 * it existed kept serving that entry: the chip asked for a key that was not
 * there, decided the number was still loading, and shimmered forever without
 * ever refetching. Validating the shape turns a stale entry into a miss.
 */
const COUNT_KEYS = ['hiring', 'firing', 'both', 'planned', 'any', 'statedHiring', 'statedFiring', 'statedBoth'];
const usableCounts = (c) => Boolean(c) && COUNT_KEYS.every((k) => typeof c[k] === 'number');

// Department vocabulary + counts, cached for the tab alongside the industries.
const DEPARTMENTS_KEY = 'cc_publicsignals_departments';
const SKILLS_KEY = 'cc_publicsignals_skills';
const LOCATIONS_KEY = 'cc_publicsignals_locations';

const PublicSignals = () => {
    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    // Read once: three pieces of state seed from the same cache entry, and
    // reading it per-initialiser would let them disagree across the TTL boundary.
    const cachedIndustries = useRef(readIndustriesCache()).current;
    const [industries, setIndustries] = useState(() => cachedIndustries?.industries || []);
    const [industriesLoading, setIndustriesLoading] = useState(() => !cachedIndustries);
    // The board's freshness ceiling, from the server so the two cannot drift.
    const [maxAgeDays, setMaxAgeDays] = useState(() => cachedIndustries?.maxAgeDays || 400);

    /**
     * The department vocabulary with its counts, for the picker and the chip.
     *
     * Same tab-lifetime cache as the industries: it is another multi-second
     * aggregation whose answer only moves when the weekly sweep runs.
     */
    const [departments, setDepartments] = useState(() => readCache(DEPARTMENTS_KEY, 'departments')?.departments || []);

    /**
     * Skill families with company counts.
     *
     * Read with a plain parse rather than `readCache`: that helper insists on a
     * `planned` key, which belongs to the counts payload and has no meaning
     * here.
     */
    const [skills, setSkills] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem(SKILLS_KEY) || 'null')?.skills || []; }
        catch { return []; }
    });

    // Where these companies employ. The widest of the optional filters — 1,136
    // companies — because "US-listed" describes the exchange, not the workforce.
    const [locations, setLocations] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem(LOCATIONS_KEY) || 'null')?.locations || []; }
        catch { return []; }
    });

    const [modalOpen, setModalOpen] = useState(false);

    const [criteria, setCriteria] = useState(() => reconcileEvidence(criteriaFromParams(searchParams)));

    /**
     * Everything that describes one response, written together.
     *
     * Held as a single object because these four values must always agree: the
     * rows, how many matched, how many pages that is, and which direction they
     * describe. As separate state they could — and did — disagree, showing a
     * hiring result set under Both's columns with a page count from neither.
     */
    const [result, setResult] = useState({ companies: [], total: 0, pageCount: 1, direction: 'hiring' });
    const { companies, total, pageCount } = result;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState(() => searchParams.get('q') || '');

    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState(() => {
        const { page, pageSize } = pagingFromParams(searchParams);
        return { pageIndex: page, pageSize };
    });

    useEffect(() => {
        // Already seeded from the cache — Back should not pay for this again.
        if (cachedIndustries) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/api/publicsignals/industries');
                if (!cancelled && data?.success) {
                    setIndustries(data.industries || []);
                    if (data.maxAgeDays) setMaxAgeDays(data.maxAgeDays);
                    try {
                        sessionStorage.setItem(INDUSTRIES_KEY, JSON.stringify({
                            at: Date.now(),
                            industries: data.industries || [],
                            maxAgeDays: data.maxAgeDays
                        }));
                    } catch { /* quota or private mode — the fetch still succeeded */ }
                }
            } catch {
                if (!cancelled) setError('Could not load industries.');
            } finally {
                if (!cancelled) setIndustriesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [cachedIndustries]);

    useEffect(() => {
        if (departments.length > 0) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/api/publicsignals/departments');
                if (!cancelled && data?.success) {
                    setDepartments(data.departments || []);
                    try {
                        sessionStorage.setItem(DEPARTMENTS_KEY, JSON.stringify({ at: Date.now(), departments: data.departments || [] }));
                    } catch { /* private mode */ }
                }
            } catch { /* the board works without the picker; no banner for this */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (skills.length > 0) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/api/publicsignals/skills');
                if (!cancelled && data?.success) {
                    setSkills(data.skills || []);
                    try {
                        sessionStorage.setItem(SKILLS_KEY, JSON.stringify({ at: Date.now(), skills: data.skills || [] }));
                    } catch { /* private mode */ }
                }
            } catch { /* optional picker */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (locations.length > 0) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/api/publicsignals/locations');
                if (!cancelled && data?.success) {
                    setLocations(data.locations || []);
                    try {
                        sessionStorage.setItem(LOCATIONS_KEY, JSON.stringify({ at: Date.now(), locations: data.locations || [] }));
                    } catch { /* private mode */ }
                }
            } catch { /* optional picker */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Load one page of results.
     *
     * @param next      search criteria (industries + direction)
     * @param page      zero-based page index
     * @param pageSize  rows per page
     *
     * Paging refetches rather than slicing locally: the server ranks the whole
     * result set — corroboration ordering is a property of the set, not of a
     * page — then sends only the page being viewed. Sending all 2,064 entries to
     * render 100 was what made the page heavy.
     */
    const fetchSignals = useCallback(async (requested, page = 0, pageSize = 100) => {
        // Coerced here rather than at each call site, so the chips, the modal and
        // a pasted URL all land on the same rule.
        const next = reconcileEvidence(requested);
        setCriteria(next);
        setPagination({ pageIndex: page, pageSize });
        // `replace` so paging does not fill the history stack — Back should leave
        // the board, not walk back through every page visited on it.
        setSearchParams({
            direction: next.direction,
            ...(next.industries?.length ? { industries: next.industries.join(',') } : {}),
            ...(next.departments?.length ? { departments: next.departments.join(',') } : {}),
            ...(next.skills?.length ? { skills: next.skills.join(',') } : {}),
            ...(next.locations?.length ? { locations: next.locations.join(',') } : {}),
            ...(next.evidence && next.evidence !== 'any' ? { evidence: next.evidence } : {}),
            ...(next.query ? { q: next.query } : {}),
            ...(next.freshDays ? { freshDays: String(next.freshDays) } : {}),
            ...(next.exchanges?.length ? { exchanges: next.exchanges.join(',') } : {}),
            ...(page ? { page: String(page) } : {}),
            ...(pageSize !== 100 ? { limit: String(pageSize) } : {}),
        }, { replace: true });
        setSearch(next.query || '');
        setModalOpen(false);
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get('/api/publicsignals/signals', {
                params: {
                    direction: next.direction,
                    industries: (next.industries || []).join(','),
                    departments: (next.departments || []).join(','),
                    skills: (next.skills || []).join(','),
                    locations: (next.locations || []).join(','),
                    evidence: next.evidence || 'any',
                    q: next.query || '',
                    freshDays: next.freshDays || 0,
                    exchanges: (next.exchanges || []).join(','),
                    page,
                    limit: pageSize,
                },
            });
            if (data?.success) {
                setResult({
                    companies: data.companies || [],
                    total: data.total || 0,
                    // The server knows the count because it ranks the whole set;
                    // deriving it from the returned page would always give 1.
                    pageCount: data.pageCount || Math.max(1, Math.ceil((data.total || 0) / pageSize)),
                    direction: next.direction,
                });
            } else {
                setError(data?.message || 'Could not load signals.');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Could not load signals.');
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * The chip totals, under the filters currently in force.
     *
     * Previously these came from the industries endpoint, which knows only about
     * industries — so narrowing to "filed < 3 months, NYSE only" cut the table to
     * 429 rows while the chips went on claiming 2,064. The summary was describing
     * a different query from the one on screen.
     *
     * Keyed on the FILTERS ALONE, not the direction or the page: one response
     * carries all three directions, so switching Hiring/Firing/Both or paging
     * reuses it rather than refetching. Cached at module scope so returning from
     * a company page is instant.
     */
    const countsKey = useMemo(() => JSON.stringify({
        i: [...(criteria?.industries || [])].sort(),
        d: [...(criteria?.departments || [])].sort(),
        s: [...(criteria?.skills || [])].sort(),
        l: [...(criteria?.locations || [])].sort(),
        q: criteria?.query || '',
        f: criteria?.freshDays || 0,
        e: [...(criteria?.exchanges || [])].sort(),
    }), [criteria]);

    const [counts, setCounts] = useState(() => {
        const c = COUNTS_CACHE.get(countsKey);
        return usableCounts(c) ? c : null;
    });
    const [countsLoading, setCountsLoading] = useState(() => !usableCounts(COUNTS_CACHE.get(countsKey)));

    useEffect(() => {
        const cached = COUNTS_CACHE.get(countsKey);
        if (usableCounts(cached)) {
            setCounts(cached);
            setCountsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setCountsLoading(true);
        (async () => {
            try {
                const { data } = await api.get('/api/publicsignals/counts', {
                    params: {
                        industries: (criteria?.industries || []).join(','),
                        departments: (criteria?.departments || []).join(','),
                        skills: (criteria?.skills || []).join(','),
                        locations: (criteria?.locations || []).join(','),
                        q: criteria?.query || '',
                        freshDays: criteria?.freshDays || 0,
                        exchanges: (criteria?.exchanges || []).join(','),
                    },
                });
                if (!cancelled && data?.success) {
                    const next = {
                        hiring: data.hiring || 0, firing: data.firing || 0,
                        both: data.both || 0, planned: data.planned || 0,
                        any: data.any || 0,
                        statedHiring: data.statedHiring || 0,
                        statedFiring: data.statedFiring || 0,
                        statedBoth: data.statedBoth || 0
                    };
                    COUNTS_CACHE.set(countsKey, next);
                    setCounts(next);
                }
            } catch {
                // Deliberately silent: the rows are the page, and a failed
                // summary must not put an error banner over a working table.
                if (!cancelled) setCounts(null);
            } finally {
                if (!cancelled) setCountsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // criteria is the source of countsKey — depending on both would refetch
        // on every direction change, which the counts do not vary with.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countsKey]);

    /**
     * Debounced so a fetch fires per pause, not per keystroke.
     *
     * Searching has to be server-side to reach every page rather than filtering
     * the hundred rows already loaded — but that makes each character a query
     * against 6,292 company names, so it waits for the typing to settle.
     */
    useEffect(() => {
        if (search === (criteria.query || '')) return;
        const t = setTimeout(() => fetchSignals({ ...criteria, query: search }, 0, pagination.pageSize), 350);
        return () => clearTimeout(t);
    }, [search, criteria, pagination.pageSize, fetchSignals]);

    // Load the sample as soon as the page opens.
    useEffect(() => {
        const { page, pageSize } = pagingFromParams(searchParams);
        fetchSignals(criteriaFromParams(searchParams), page, pageSize);
        // Mount only: later fetches drive the URL, not the other way round.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Restore the scroll position once the rows that justify it exist.
     *
     * Restoring on mount would set scrollTop on an empty container and be
     * clamped to zero, so it waits for the data. Cleared after use: a fresh
     * visit should start at the top.
     */
    useEffect(() => {
        if (loading || companies.length === 0) return;
        const saved = Number(sessionStorage.getItem(SCROLL_KEY) || 0);
        if (saved > 0 && scrollRef.current) {
            scrollRef.current.scrollTop = saved;
            sessionStorage.removeItem(SCROLL_KEY);
        }
    }, [loading, companies.length]);

    const openCompany = useCallback((ticker) => {
        if (scrollRef.current) sessionStorage.setItem(SCROLL_KEY, String(scrollRef.current.scrollTop));
        navigate(`/hiring-signals-us/${ticker}`);
    }, [navigate]);

    const isPlanned = result.direction === 'planned';
    // `planned` is firing narrowed to cuts not yet carried out, so it keeps
    // firing's red columns and copy.
    const isFiring = result.direction === 'firing' || isPlanned;
    const isBoth = result.direction === 'both';
    // Under 'both' each row carries its own direction; otherwise the whole list
    // shares the requested one.
    const rowIsFiring = (row) => (isBoth ? row.direction === 'firing' : isFiring);

    // No local filtering: the server already applied the query across the whole
    // result set, so re-filtering here would only hide matching rows.
    const filtered = companies;



    /*
     * Saved tickers, so a row can show whether it is already bookmarked.
     *
     * Read from the existing saved-companies endpoint rather than a new one:
     * a saved public company IS a saved company, and its Companies row carries
     * publiclyTradedSymbol, which is the ticker this board is keyed on.
     */
    /*
     * Table is the default; Signals is one click away.
     *
     * Signals answers "what did they actually say" and is the better read once
     * you have filtered down, but the table is what a reader arriving at 2,377
     * companies needs first — sortable columns and fifty rows to a screen.
     *
     * The choice lives in the URL, so a filtered, shared link keeps whichever
     * view was meant.
     */
    const [view, setView] = useState(() => (searchParams.get('view') === 'signals' ? 'signals' : 'table'));

    // Which company is expanded inline, by ticker. One at a time: two open
    // detail pages in a table push everything else off the screen.
    const [expanded, setExpanded] = useState(null);

    const [savedByTicker, setSavedByTicker] = useState({});
    const [savingTicker, setSavingTicker] = useState(null);
    const user = useSelector((st) => st.auth.user);

    useEffect(() => {
        if (!user?._id) return;
        let cancelled = false;
        (async () => {
            try {
                const { data: rows } = await api.get(`/api/savedcompanies/getsavedcompanies/${user._id}`);
                if (cancelled || !Array.isArray(rows)) return;
                const map = {};
                rows.forEach((r) => {
                    const sym = r.companyId?.publiclyTradedSymbol;
                    if (sym) map[sym] = r._id;
                });
                setSavedByTicker(map);
            } catch { /* board still works unsaved */ }
        })();
        return () => { cancelled = true; };
    }, [user?._id]);

    const toggleSaved = useCallback(async (ticker) => {
        if (savingTicker) return;
        setSavingTicker(ticker);
        const existing = savedByTicker[ticker];
        try {
            if (existing) {
                await api.delete(`/api/savedcompanies/deletesavedcompany/${existing}`);
                setSavedByTicker((m) => { const n = { ...m }; delete n[ticker]; return n; });
            } else {
                const { data: res } = await api.post(`/api/publicsignals/save/${encodeURIComponent(ticker)}`);
                if (res?.success) setSavedByTicker((m) => ({ ...m, [ticker]: res.savedId }));
            }
        } catch { /* leave the previous state visible */ }
        finally { setSavingTicker(null); }
    }, [savedByTicker, savingTicker]);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (view === 'signals') next.set('view', 'signals'); else next.delete('view');
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);

    const columnHelper = useMemo(() => createColumnHelper(), []);

    // With a single industry selected the column repeats one word down every
    // row; the chip in the header already says which. Dropped so the space goes
    // to the columns that differ.
    const showIndustry = (criteria?.industries?.length || 0) !== 1;

    const columns = useMemo(() => [
        columnHelper.accessor((r) => r.companyName || '', {
            id: 'company',
            size: 170,
            sortDescFirst: false,
            meta: { label: 'Company', icon: Building, pill: 'bg-slate-700' },
            cell: ({ row }) => {
                const c = row.original;
                return (
                    <div className="flex items-center gap-2 min-w-0 overflow-hidden" style={{ maxWidth: 190 }}>
                        {renderBookmark({
                            isSaved: Boolean(savedByTicker[c.ticker]),
                            isSaving: savingTicker === c.ticker,
                            onToggle: () => toggleSaved(c.ticker)
                        })}
                        {renderCompanyAvatar(null, c.companyName)}
                        <div className="min-w-0">
                            {/* Full name on hover — truncating is the point, but a reader
                                should still be able to read it without leaving the page. */}
                            <div className="text-[13px] font-semibold text-gray-900 truncate" title={c.companyName}>
                                {c.companyName}
                            </div>
                            <div className="text-[10px] font-mono text-gray-500">{c.ticker}</div>
                        </div>
                    </div>
                );
            },
        }),
        /**
         * The headcount itself. "employees" lives in the header — it was
         * identical on every row and cost the column a third of its width.
         */
        columnHelper.accessor((r) => {
            const m = String(r.magnitude || '').match(/^([\d,]+)\s+(?:full[- ]time\s+)?(?:employees|people|team members)/i);
            return m ? Number(m[1].replace(/,/g, '')) : -1;
        }, {
            id: 'headcount',
            size: 120,
            sortDescFirst: true,
            meta: { label: 'Employees', icon: Users, pill: 'bg-violet-600' },
            cell: ({ row }) => renderHeadcount(row.original, rowIsFiring(row.original)),
        }),
        /**
         * Which way it moved. Sorted on the percentage so the biggest movers
         * lead, rather than alphabetically on a formatted string.
         */
        columnHelper.accessor((r) => {
            const m = String(r.magnitude || '').match(/([+-]?\d+(?:\.\d+)?)%/);
            return m ? Math.abs(Number(m[1])) : -1;
        }, {
            id: 'change',
            size: 110,
            sortDescFirst: true,
            meta: { label: '1 yr change', icon: ArrowUpDown, pill: 'bg-violet-500' },
            cell: ({ row }) => renderDelta(row.original, rowIsFiring(row.original)),
        }),
        /**
         * How the claim is evidenced. Its own column rather than a badge tacked
         * onto the headcount cell — it is a property of the row, not of the
         * number, and crowding it beside a figure and a delta pill left all
         * three fighting for the same 185px.
         *
         * Named "Basis" because "Evidence" is already taken by the column
         * listing which documents the signal came from.
         *
         * Dropped entirely under `planned`: every row there reads "Stated",
         * since a pending cut exists only because the company announced one. A
         * column with one value carries no information and costs 110px in the
         * view that can least afford it.
         */
        ...(isPlanned ? [] : [columnHelper.accessor((r) => (isFiring ? r.statesReduction : r.statesHiring) ? 1 : 0, {
            id: 'basis',
            size: 110,
            sortDescFirst: true,
            meta: { label: 'Basis', icon: FileText, pill: 'bg-slate-600' },
            cell: ({ row }) => (
                <div className="flex justify-center">
                    <EvidenceKind
                        firing={isFiring}
                        states={isFiring ? row.original.statesReduction : row.original.statesHiring}
                    />
                </div>
            ),
        })]),
        ...(isBoth ? [columnHelper.accessor((r) => r.direction || '', {
            id: 'direction',
            size: 105,
            sortDescFirst: false,
            meta: { label: 'Signal', icon: ArrowUpDown, pill: 'bg-slate-500' },
            // Only present when the list mixes directions — with a single
            // direction every row would read the same.
            cell: ({ row }) => {
                const c = row.original;
                const firing = c.direction === 'firing';
                return (
                    <div className="flex flex-col items-center gap-0.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${firing ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {firing ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                            {firing ? 'Cutting' : 'Hiring'}
                        </span>
                        {/* Every company here is doing both; the badge says which
                            side its strongest evidence falls on, and this names the
                            other side. Coloured for the direction it names, not for
                            the row — in amber both lines read as one warning, and
                            "+ hiring" under a red Cutting badge looked like more bad
                            news when it is the opposite. */}
                        <span className={`text-[9px] font-semibold ${firing ? 'text-emerald-600' : 'text-red-600'}`}>
                            + {firing ? 'hiring' : 'cutting'}
                        </span>
                    </div>
                );
            },
        })] : []),
        ...(isPlanned ? [columnHelper.accessor((r) => {
            // Undated rows sort last rather than first: a stated deadline is
            // more actionable than none, whichever way the column is sorted.
            const d = r.pendingCut?.expectedBy;
            return d ? new Date(d).getTime() : Number.MAX_SAFE_INTEGER;
        }, {
            id: 'due',
            size: 132,
            sortDescFirst: false,
            meta: { label: 'Cut due by', icon: Clock, pill: 'bg-amber-600' },
            /**
             * The whole point of this view: the cut is stated and not finished,
             * so this is how long there is to reach the company before the staff
             * are gone. Most give no date — shown as "no date given" rather than
             * a dash, because the absence is information, not a missing value.
             */
            cell: ({ row }) => {
                const p = row.original.pendingCut;
                if (!p) return <span className="text-[12px] text-gray-300">—</span>;
                const due = p.expectedBy ? new Date(p.expectedBy) : null;
                const months = due ? Math.round((due - Date.now()) / 2629800000) : null;
                return (
                    <div className="whitespace-nowrap leading-tight">
                        <div className="text-[12px] font-semibold text-amber-700">
                            {/* Rendered in UTC, deliberately. The deadline is stored as
                                the last instant of its month, so formatting it in a zone
                                ahead of UTC rolls it into the next one — Goodyear's "by
                                the end of 2027" displayed as Jan 2028 in IST. The month
                                is the whole content of this field; it must not drift. */}
                            {due
                                ? due.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
                                : <span className="font-medium text-gray-400">no date given</span>}
                        </div>
                        <div className="text-[10px] text-gray-400">
                            {due ? (months <= 0 ? 'this month' : `in ~${months} mo`) : p.status}
                        </div>
                    </div>
                );
            },
        })] : []),
        /**
         * How many people the cut affects.
         *
         * Its own column because these run to a full sentence — "350 team
         * members, 14% of workforce, exit 22 countries, reduce geographic
         * footprint by 37%" — which no single line under the date could hold.
         * Allowed to wrap rather than truncate: the detail IS the value here,
         * and a reader scanning for scale should not have to hover every row.
         */
        ...(isPlanned ? [columnHelper.accessor((r) => r.pendingCut?.peopleAffected || '', {
            id: 'affected',
            size: 178,
            sortDescFirst: false,
            meta: { label: 'How many', icon: Users, pill: 'bg-amber-700' },
            cell: ({ row }) => {
                const text = row.original.pendingCut?.peopleAffected;
                if (!text) return <span className="text-[12px] text-gray-300">not stated</span>;
                return (
                    <div className="text-[11.5px] text-gray-600 leading-snug whitespace-normal break-words">
                        {text}
                    </div>
                );
            },
        })] : []),
        ...(showIndustry ? [columnHelper.accessor((r) => r.industry || '', {
            id: 'industry',
            size: 128,
            sortDescFirst: false,
            meta: { label: 'Industry', icon: Building, pill: 'bg-amber-500' },
            cell: ({ row }) => (
                <span className="text-[12px] text-gray-700 leading-tight block" title={row.original.sicDescription || ''}>
                    {row.original.industry || '—'}
                </span>
            ),
        })] : []),
        columnHelper.accessor((r) => r.exchange || '', {
            id: 'exchange',
            size: 74,
            sortDescFirst: false,
            meta: { label: 'Listed', icon: MapPin, pill: 'bg-teal-600' },
            cell: ({ row }) => <span className="text-[12px] text-gray-700">{row.original.exchange || '—'}</span>,
        }),
        columnHelper.accessor((r) => (r.sources || []).length, {
            id: 'sources',
            size: 130,
            sortDescFirst: true,
            // Sorted by count: the number of independent sources agreeing is the
            // strongest claim this board can make.
            meta: { label: 'Evidence', icon: FileText, pill: 'bg-indigo-600' },
            cell: ({ row }) => (
                <div className="flex items-center gap-1 flex-wrap justify-center">
                    {(row.original.sources || []).map((s, i) => {
                        const meta = SOURCE_META[s.docType] || { label: s.docType, tone: 'bg-gray-100 text-gray-600' };
                        return (
                            <span key={`${s.docType}-${i}`} title={meta.title}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.tone}`}>
                                {meta.label}
                            </span>
                        );
                    })}
                </div>
            ),
        }),
        columnHelper.accessor((r) => (r.documentDate ? new Date(r.documentDate).getTime() : 0), {
            id: 'filed',
            size: 110,
            sortDescFirst: true,
            meta: { label: 'Filed', icon: Clock, pill: 'bg-rose-500' },
            // The FILING's age, not when we scanned. A signal drawn from a
            // seven-month-old annual report is seven months old, and hiding that
            // behind a scan date would overstate how current it is. The period it
            // covers is shown beneath so the age is attributable to something.
            cell: ({ row }) => {
                const c = row.original;
                const period = (c.sources || [])[0]?.periodKey;
                return (
                    <div className="whitespace-nowrap leading-tight">
                        <div className="text-[12px] text-gray-600">
                            {c.documentDate ? getDaysAgo(c.documentDate) : '—'}
                        </div>
                        {/*
                            The period the document covers — "Q1 2027", "FY 2026".
                            An 8-K has no reporting period, so the sweep stores its
                            SEC accession number there to keep rows unique;
                            "0001628280-26-049010" is a database key that was never
                            meant to be read, and it appeared under a third of the
                            rows. Shown only when it means something.
                        */}
                        {period && !/^\d{10}-\d{2}-\d{6}$/.test(period) && (
                            <div className="text-[10px] text-gray-400">{period}</div>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            /**
             * Wider than it looks like it needs.
             * The cell carries 32px of padding (pl-3 + pr-5), so at the old
             * ratio the button had ~99px for content that needs ~100 — and the
             * one column a row exists to reach was clipped by a pixel.
             */
            size: 215,
            meta: { label: 'Details', pill: 'bg-emerald-600' },
            // Opens the full page rather than expanding in place: a company holds
            // up to ten signals across four documents and several quarters, which
            // is more than a table row can carry legibly.
            cell: ({ row }) => (
                <div className="flex items-center justify-center gap-1.5">
                <button
                    onClick={(e) => { e.stopPropagation(); openCompany(row.original.ticker); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-[#3F57E0] border border-[#3F57E0] rounded-md hover:bg-[#EEF1FE] transition-colors whitespace-nowrap"
                >
                    View Details <ChevronRight className="w-3 h-3" />
                </button>
                {/* See more — expands the full company page inline below the row,
                    the same affordance the jobs list uses. */}
                <button
                    onClick={(e) => { e.stopPropagation(); setExpanded((t) => (t === row.original.ticker ? null : row.original.ticker)); }}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border transition-colors whitespace-nowrap ${
                        expanded === row.original.ticker
                            ? 'border-[#3F57E0] bg-[#EEF1FE] text-[#3F57E0]'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-[#C7D2FE] hover:text-[#3F57E0]'
                    }`}
                >
                    {expanded === row.original.ticker ? 'See less' : 'See more'}
                    <ChevronDown className={`w-3 h-3 transition-transform ${expanded === row.original.ticker ? 'rotate-180' : ''}`} />
                </button>
                </div>
            ),
        }),
    ], [columnHelper, isFiring, isBoth, isPlanned, rowIsFiring, openCompany, showIndustry, savedByTicker, savingTicker, toggleSaved, expanded]);

    // Derived from the columns actually rendered rather than hard-coded: `both`
    // adds a Signal column, a single-industry filter removes Industry.
    /**
     * Denominator for the proportional widths — the sum of every visible
     * column's `size`, which is a ratio now rather than a pixel count.
     */
    const tableMinWidth = useMemo(
        () => columns.reduce((sum, c) => sum + (c.size || 120), 0),
        [columns]
    );

    /**
     * The width below which the table stops shrinking and starts scrolling.
     *
     * Fixed rather than derived from the column count. The planned view has two
     * more columns than hiring, and letting its floor rise with them is exactly
     * what forced the sideways scroll; below this the columns are unreadable
     * anyway, so scrolling is the right answer there.
     */
    const TABLE_FLOOR_PX = 940;

    const table = useReactTable({
        data: filtered,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        // No pagination row model: the server sends one page, so slicing again
        // client-side would show a page of a page.
    });

    /**
     * Companies this board would show, under the evidence filter in force.
     *
     * Was `counts.any`, which spans every direction. The chip that used to carry
     * the hiring number is gone, so this is now the only place the figure
     * appears and it has to be the right one.
     */
    const hiringTotal = useMemo(() => {
        if (!counts) return null;
        if (criteria?.evidence === 'stated') return counts.statedHiring ?? null;
        if (criteria?.evidence === 'measured') {
            return typeof counts.statedHiring === 'number' ? counts.hiring - counts.statedHiring : null;
        }
        return counts.hiring ?? null;
    }, [counts, criteria]);

    const industryLabel = !criteria ? ''
        : criteria.industries.length === 0 ? 'All industries'
            : criteria.industries.length === 1 ? criteria.industries[0]
                : `${criteria.industries.length} industries`;

    const departmentLabel = !criteria ? ''
        : criteria.departments.length === 0 ? 'All departments'
            : criteria.departments.length === 1 ? criteria.departments[0]
                : `${criteria.departments.length} departments`;

    /**
     * The industries behind a collapsed "3 industries" chip.
     *
     * The chip has to collapse — three or four names would push the direction
     * toggle off the row — but collapsing it hid the single most important fact
     * about the result set, and the only way to recall the selection was to
     * reopen the filter modal. Each name carries its count for the direction
     * currently being viewed, so the breakdown of the total is visible too.
     */
    const industryBreakdown = useMemo(() => {
        if (!criteria || criteria.industries.length < 2) return [];
        const dir = criteria.direction;
        /**
         * The per-industry figures are the whole-window, all-venues ones — the
         * industries endpoint takes no other filters. Shown only when nothing
         * else is narrowing the board, because under "filed < 3 months, NYSE"
         * they would be numbers from a different question sitting under a
         * heading that says "filtered to". Names alone are still useful; wrong
         * counts are not.
         */
        const trustworthy = !criteria.freshDays && !(criteria.exchanges?.length) && !criteria.query;
        return criteria.industries.map((name) => ({
            name,
            n: trustworthy ? (industries.find((i) => i.name === name)?.[dir] ?? null) : null
        }));
    }, [criteria, industries]);

    return (
        // Navbaar is fixed, so it occupies no flex space — pt-16 clears it and
        // overflow-hidden keeps scrolling inside the table rather than moving the
        // whole page, which was pushing the header and sidebar off the top.
        <div className="relative h-screen bg-[#FCFCFC] pt-16 flex flex-col overflow-hidden text-left">
            <Navbaar />

            <PublicSignalFetchModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onFetch={(next) => fetchSignals({ ...next, query: criteria.query || '' }, 0, pagination.pageSize)}
                industries={industries}
                departments={departments}
                skills={skills}
                locations={locations}
                loading={loading}
                defaultIndustries={criteria?.industries || []}
                defaultDepartments={criteria?.departments || []}
                defaultSkills={criteria?.skills || []}
                defaultLocations={criteria?.locations || []}
                defaultDirection={criteria?.direction || 'hiring'}
                defaultFreshDays={criteria?.freshDays || 0}
                defaultExchanges={criteria?.exchanges || []}
                maxAgeDays={maxAgeDays}
            />

            <div className="flex flex-1 min-h-0 transition-all duration-300">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {/* Header band — mirrors Hiring Signals */}
                    <div className="px-6 pt-5 pb-3 border-b border-gray-100 bg-white shrink-0">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            {/* min-w-0 keeps the title block anchored left when the controls
                                beside it wrap — without it the flex row redistributes and the
                                heading drifts toward centre. */}
                            <div className="min-w-0">
                                <h1 className="text-[20px] font-bold text-gray-900">Hiring Signals (US)</h1>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    US-listed companies · hiring from SEC filings and earnings calls
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {criteria && (
                                    <>
                                        {/*
                                            How the claim is evidenced.
                                            59% of the hiring list and 57% of the firing list are
                                            companies whose headcount moved with nothing said about
                                            it — a rise can be an acquisition, a fall can be
                                            attrition. Both are real signals, so "All" is the
                                            default and neither half is hidden unless asked for.
                                            Shown under `planned` too, for a consistent control,
                                            but Measured is disabled there: an unfinished cut is
                                            one the company stated, so it could only ever be empty.
                                        */}
                                        <div className="inline-flex p-0.5 bg-gray-100 rounded-full">
                                            {[
                                                { key: 'any', label: 'All' },
                                                { key: 'stated', label: 'Stated' },
                                                { key: 'measured', label: 'Measured' },
                                            ].map((e) => {
                                                const active = (criteria.evidence || 'any') === e.key;
                                                const impossible = isPlanned && e.key === 'measured';
                                                return (
                                                    <button
                                                        key={e.key}
                                                        disabled={loading || active || impossible}
                                                        onClick={() => fetchSignals({ ...criteria, evidence: e.key }, 0, pagination.pageSize)}
                                                        title={impossible
                                                            ? 'Not applicable — every unfinished cut is one the company stated'
                                                            : e.key === 'stated'
                                                                ? 'Only companies that say so in a filing or on an earnings call'
                                                                : e.key === 'measured'
                                                                    ? 'Only companies whose headcount moved with no statement about it'
                                                                    : 'Both kinds of evidence'}
                                                        className={`px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold transition
                                                            ${active ? 'bg-white text-gray-900 shadow-sm'
                                                                : impossible ? 'text-gray-300 cursor-not-allowed'
                                                                    : 'text-gray-500 hover:text-gray-800'}`}
                                                    >
                                                        {e.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="relative">
                                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                placeholder="Search all companies…"
                                                className="pl-9 pr-3 py-2 w-64 rounded-lg border border-gray-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#3F57E0]/15"
                                            />
                                        </div>
                                        <button onClick={() => criteria && fetchSignals(criteria, pagination.pageIndex, pagination.pageSize)} disabled={loading}
                                            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50" title="Refresh">
                                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                                        </button>
                                    </>
                                )}
                                <button
                                    onClick={() => setModalOpen(true)}
                                    disabled={industriesLoading}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#3F57E0] text-white text-[13px] font-semibold hover:bg-[#3548c8] disabled:opacity-60 whitespace-nowrap"
                                >
                                    <Landmark size={14} />
                                    Filters
                                </button>

                                {/*
                                    Table scans the companies, Signals reads what they said.
                                    Both draw the same filtered rows from the same request —
                                    this only decides how much of each row is shown. It sits
                                    beside Filters because it is the same kind of control:
                                    what you are looking at, not what you are looking for.
                                */}
                                <div className="flex items-center gap-1 p-[3px] rounded-lg bg-[#F1F3F7] shrink-0"
                                     title={VIEW_HINT}>
                                    {[
                                        ['table', 'Table', 'One row per company — sortable columns, many companies at a glance.'],
                                        ['signals', 'Cards', "Same companies, opened out — each one's filings and what it said."],
                                    ].map(([key, label, what]) => (
                                        <button
                                            key={key}
                                            onClick={() => setView(key)}
                                            title={`${what}\n\n${VIEW_HINT}`}
                                            className={`text-[12.5px] font-bold px-3 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                                                view === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {criteria && (
                            <div className="mt-3.5 flex items-center gap-2 flex-wrap">
                                {/* Distinct companies with any signal, not the current
                                    direction's count — hiring and firing overlap by the 452
                                    doing both, so adding them would claim 3,874 companies
                                    where 3,422 exist. The per-direction figure lives on the
                                    toggle beside it. */}
                                <span className="text-[13px] font-bold text-gray-900">
                                    Signal Listings{' '}
                                    {/*
                                        The HIRING total, not `any`.
                                        `any` counts every company with a signal in any
                                        direction — it read 3,561 above a list of 1,125,
                                        because it was still counting the firing and
                                        both-direction companies this board no longer shows.
                                        Follows the evidence toggle for the same reason.
                                    */}
                                    {countsLoading || !counts || hiringTotal === null ? (
                                        <span className="inline-block align-middle w-20 h-3 rounded cc-skel" />
                                    ) : (
                                        <span className="text-gray-400 font-medium">
                                            {hiringTotal.toLocaleString()} companies
                                        </span>
                                    )}
                                </span>
                                {industryBreakdown.length > 0 ? (
                                    // tabIndex + group-focus so the list is reachable without a
                                    // pointer — a hover-only affordance is unusable on touch and
                                    // by keyboard.
                                    <span
                                        tabIndex={0}
                                        className={`relative group ${FILTER_CHIP} cursor-default outline-none hover:bg-[#E3E9FD] focus:ring-2 focus:ring-[#3F57E0]/40 transition-colors`}
                                    >
                                        {industryLabel}
                                        <span className="ml-1 text-[#3F57E0]/45 font-normal">▾</span>
                                        <span className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden group-hover:block group-focus:block min-w-[210px] rounded-lg border border-gray-200 bg-white p-2.5 shadow-lg text-left">
                                            <span className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">
                                                Filtered to
                                            </span>
                                            {industryBreakdown.map((b) => (
                                                <span key={b.name} className="flex items-center justify-between gap-4 py-[3px]">
                                                    <span className="text-[12px] font-medium text-gray-700">{b.name}</span>
                                                    <span className="text-[11px] font-semibold text-gray-400 tabular-nums">
                                                        {b.n === null ? '—' : b.n.toLocaleString()}
                                                    </span>
                                                </span>
                                            ))}
                                            {industryBreakdown.some((b) => b.n !== null) && (
                                                <span className="block mt-1.5 pt-1.5 border-t border-gray-100 text-[10px] text-gray-400">
                                                    Companies {isBoth ? 'doing both' : isFiring ? 'cutting' : 'hiring'}, per industry
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                ) : (
                                    <span className={FILTER_CHIP}>
                                        {industryLabel}
                                    </span>
                                )}
                                {/* Aaron's ask, 2026-08-05: "if I'm in marketing, I need to
                                    know quickly what companies are hiring for marketing".
                                    A filter, not a per-row label — the department is only
                                    useful if it narrows the list. */}
                                {/* A statement of what is filtered, not a control. Opening the
                                    whole filter modal from it was a surprise: the chips beside
                                    it — industries, freshness — are labels, so one of them
                                    silently being a button is not something a reader would
                                    predict. Filters is where filters are changed. */}
                                <span className={FILTER_CHIP}>
                                    {departmentLabel}
                                </span>
                                {/* Only when set — 143 companies of 3,561 name a skill,
                                    so a permanent "All skills" chip would advertise a
                                    filter that is empty for almost every row. */}
                                {criteria.skills?.length > 0 && (
                                    <span className={FILTER_CHIP}>
                                        {criteria.skills.length === 1 ? criteria.skills[0] : `${criteria.skills.length} skills`}
                                    </span>
                                )}
                                {criteria.locations?.length > 0 && (
                                    <span className={FILTER_CHIP}>
                                        {criteria.locations.length === 1 ? criteria.locations[0] : `${criteria.locations.length} places`}
                                    </span>
                                )}
                                {/* Only shown when set — a chip reading "Any" every time is
                                    noise, but a narrowed result set must say so. */}
                                {/* Always shown: the ceiling applies whether or not the user
                                    narrowed it, and a list that silently excludes older
                                    filings should say so. */}
                                <span className={FILTER_CHIP}>
                                    Filed &lt; {criteria.freshDays > 0
                                        ? (criteria.freshDays >= 365
                                            ? '1 year'
                                            : `${Math.round(criteria.freshDays / 30)} month${criteria.freshDays > 30 ? 's' : ''}`)
                                        : `${Math.round(maxAgeDays / 30.44)} months`}
                                </span>
                                {criteria.exchanges?.length > 0 && (
                                    <span className={FILTER_CHIP}>
                                        {criteria.exchanges.join(' · ')}
                                    </span>
                                )}
                                <span className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] text-gray-400">
                                    <RefreshCw size={11} />
                                    Refreshed weekly from SEC filings and earnings calls
                                </span>
                            </div>
                        )}
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 pt-5 pb-20 hide-scrollbar">
                        {error && (
                            <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-[13px] text-red-700">{error}</div>
                        )}



                        {loading && (
                            // Fed from the live column set so the two can never drift:
                            // whichever columns this view shows, the placeholder shows
                            // the same ones at the same widths.
                            <TableSkeleton
                                rows={Math.min(pagination.pageSize, 8)}
                                columns={table.getVisibleLeafColumns().map((c) => ({ id: c.id, size: c.getSize() }))}
                                minWidth={tableMinWidth}
                            />
                        )}

                        {!loading && criteria && filtered.length === 0 && !error && (
                            <div className="mt-20 text-center text-gray-500">
                                <Building size={26} className="mx-auto mb-3 opacity-40" />
                                <p className="text-[13px]">
                                    No {isPlanned ? 'unfinished cuts' : isBoth ? 'signals' : isFiring ? 'firing signals' : 'hiring signals'} in these industries.
                                </p>
                                <button onClick={() => setModalOpen(true)} className="mt-3 text-[13px] font-semibold text-[#3F57E0] hover:underline">
                                    Try different industries
                                </button>
                            </div>
                        )}

                        {!loading && filtered.length > 0 && view === 'signals' && (
                            <div className="flex flex-col gap-3">
                                {filtered.map((row) => (
                                    <SignalCard key={row.ticker} row={row} onOpen={openCompany} />
                                ))}
                            </div>
                        )}

                        {!loading && filtered.length > 0 && view === 'table' && (
                            // overflow-x-auto rather than overflow-hidden: at this column
                            // count the table is wider than the content area on a laptop,
                            // and hiding the overflow silently cut off Filed and Details.
                            <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto">
                                {/* table-fixed plus explicit widths: TanStack's `size` is
                                    metadata only and applies no CSS, so columns were
                                    auto-sizing and Company took a third of the table while
                                    the right-hand columns were pushed out of view. */}
                                <table className="w-full table-fixed" style={{ minWidth: TABLE_FLOOR_PX }}>
                                    {/*
                                        Proportional, not pixel, widths.
                                        `size` is a ratio here rather than a measurement: at
                                        fixed pixels the planned view came to 1,490px and
                                        scrolled sideways on a laptop, hiding the Details
                                        button — the column the row exists to reach. As
                                        percentages of a table-fixed layout the same
                                        proportions fit whatever the container is, and the
                                        floor below only applies to genuinely narrow windows.
                                    */}
                                    <colgroup>
                                        {table.getVisibleLeafColumns().map((col) => (
                                            <col
                                                key={col.id}
                                                style={{ width: `${((col.getSize() / tableMinWidth) * 100).toFixed(3)}%` }}
                                            />
                                        ))}
                                    </colgroup>
                                    <thead>
                                        {table.getHeaderGroups().map((hg) => (
                                            <tr key={hg.id} className="bg-white border-b border-gray-200">
                                                {hg.headers.map((header, i) => {
                                                    const isFirst = i === 0;
                                                    const isLast = i === hg.headers.length - 1;
                                                    const centered = ['sources', 'actions', 'direction', 'basis'].includes(header.id);
                                                    const sorted = header.column.getIsSorted();
                                                    return (
                                                        <th key={header.id}
                                                            className={`py-3 font-normal text-left ${isFirst ? 'pl-5 pr-2' : isLast ? 'pl-3 pr-5' : 'px-3'} ${centered ? 'text-center' : ''} ${['change', 'headcount'].includes(header.id) ? 'text-right' : ''}`}
                                                            aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none'}>
                                                            <div className={`flex items-center ${centered ? 'justify-center' : ['change', 'headcount'].includes(header.id) ? 'justify-end' : ''}`}>
                                                                <HeaderPill header={header} />
                                                            </div>
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody>
                                        {table.getRowModel().rows.map((row) => {
                                            const c = row.original;
                                            const isOpen = expanded === c.ticker;
                                            return (
                                                <React.Fragment key={row.id}>
                                                    <tr
                                                        onClick={() => openCompany(c.ticker)}
                                                        className={`border-b border-gray-100 transition-colors cursor-pointer hover:bg-gray-50/60 ${isOpen ? 'bg-[#F8FAFF]' : ''}`}
                                                    >
                                                        {row.getVisibleCells().map((cell, i) => {
                                                            const isFirst = i === 0;
                                                            const isLast = i === row.getVisibleCells().length - 1;
                                                            const centered = ['sources', 'actions', 'direction', 'basis'].includes(cell.column.id);
                                                            return (
                                                                <td key={cell.id}
                                                                    className={`py-2.5 ${isFirst ? 'pl-5 pr-2' : isLast ? 'pl-3 pr-5' : 'px-3'} ${centered ? 'text-center' : ''} ${['change', 'headcount'].includes(cell.column.id) ? 'text-right' : ''} ${cell.column.id === 'affected' ? 'align-top' : ''}`}>
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>

                                                    {/*
                                                        The whole company page, inline.

                                                        colSpan over every visible column so it
                                                        spans the table rather than squeezing into
                                                        one cell, and stopPropagation because the
                                                        row above navigates on click.
                                                    */}
                                                    {isOpen && (
                                                        <tr>
                                                            <td colSpan={row.getVisibleCells().length} className="p-0 border-b border-gray-200">
                                                                <div onClick={(e) => e.stopPropagation()} className="cursor-default">
                                                                    <PublicSignalDetailPage tickerProp={c.ticker} embedded />
                                                                    <div className="flex justify-center pt-2 pb-6" style={{ background: '#F4F5FA' }}>
                                                                        <button
                                                                            onClick={() => setExpanded(null)}
                                                                            className="group inline-flex items-center gap-2 pl-5 pr-4 py-2.5 rounded-full border border-[#D9DEEA] bg-white text-[12.5px] font-bold text-[#475569] shadow-sm hover:text-[#3F57E0] hover:border-[#3F57E0] hover:shadow transition-all"
                                                                        >
                                                                            Collapse {c.ticker}
                                                                            <span className="w-5 h-5 rounded-full grid place-items-center bg-[#F1F3F7] group-hover:bg-[#EEF1FE] transition-colors">
                                                                                <ChevronDown className="w-3 h-3 rotate-180" />
                                                                            </span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!loading && filtered.length > 0 && (
                            <>
                                <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[12px] text-gray-500">Rows per page</span>
                                        <select
                                            value={pagination.pageSize}
                                            onChange={(e) => fetchSignals(criteria, 0, Number(e.target.value))}
                                            className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] font-semibold"
                                        >
                                            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                        <span className="text-[12px] text-gray-400">
                                            {(() => {
                                                const from = pagination.pageIndex * pagination.pageSize + 1;
                                                const to = pagination.pageIndex * pagination.pageSize + companies.length;
                                                return `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`;
                                            })()}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-1.5">
                                        <button
                                            onClick={() => fetchSignals(criteria, pagination.pageIndex - 1, pagination.pageSize)}
                                            disabled={loading || pagination.pageIndex === 0}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft className="w-3.5 h-3.5" /> Prev
                                        </button>
                                        <span className="px-2 text-[12px] text-gray-500 tabular-nums">
                                            {pagination.pageIndex + 1} / {pageCount}
                                        </span>
                                        <button
                                            onClick={() => fetchSignals(criteria, pagination.pageIndex + 1, pagination.pageSize)}
                                            disabled={loading || pagination.pageIndex + 1 >= pageCount}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-gray-200 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            Next <ChevronRight className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>

                                {/* One sentence per line, each its own idea: what the column
                                    measures, then what that means for the board's contents.
                                    As a single run it set as one very long line on a wide
                                    screen, which is where a footnote stops being read. */}
                                <p className="mt-3 text-[11px] leading-[1.65] text-gray-400">
                                    <span className="block">
                                        “Filed” is the age of the SEC document itself, not when you ran this
                                        search — an annual report covering FY 2025 is naturally several months old.
                                    </span>
                                    <span className="block">
                                        Signals from documents older than {Math.round(maxAgeDays / 30.44)} months
                                        are excluded, so a company that has stopped filing drops off the board.
                                    </span>
                                    <span className="block mt-1.5">
                                        <span className="font-semibold text-gray-500">Stated</span> — the company says it is
                                        {isFiring ? ' reducing headcount' : ' hiring'}.{' '}
                                        <span className="font-semibold text-gray-500">Measured</span> — headcount
                                        {isFiring
                                            ? ' fell year over year, but nothing says anyone was cut; it may be attrition, a hiring freeze or a business being sold.'
                                            : ' rose year over year, but nothing says anyone was hired; it may be an acquisition rather than open roles.'}
                                    </span>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicSignals;
